export const declarationsEn = {
  title: 'Declarations and allergens',
  subtitle: 'Multilingual, versioned proposals from approved recipes with controlled legal review.',
  help: 'Open help for declarations, catalogue terms and approvals',
  empty: 'No declaration version matches the selected filters.',
  pagination: 'Page {{page}} of {{pages}} · {{total}} versions',
  actions: {
    catalog: 'Domain catalogue',
    generate: 'Generate proposal',
    newTerm: 'Catalogue entry',
    submit: 'Submit for review',
    review: 'Complete review',
    approve: 'Approve',
    publish: 'Publish',
    archive: 'Archive',
    copy: 'New version',
    override: 'Document override',
    confirm: 'Confirm',
    history: 'Versions',
  },
  filters: {
    search: 'Search number, product, market or term',
    allStatuses: 'All statuses',
    allTypes: 'All types',
  },
  statuses: {
    DRAFT: 'Draft',
    VALIDATION_ERROR: 'Validation error',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    PUBLISHED: 'Published',
    SUPERSEDED: 'Superseded',
    ARCHIVED: 'Archived',
  },
  catalogStatuses: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    SUPERSEDED: 'Superseded',
    ARCHIVED: 'Archived',
  },
  allergenStatuses: {
    CONTAINS: 'Contains',
    MAY_CONTAIN: 'May contain',
    DOES_NOT_CONTAIN: 'Does not contain',
    UNKNOWN: 'Unknown',
  },
  issueSeverity: { ERROR: 'Error', WARNING: 'Warning' },
  types: {
    INGREDIENT: 'Ingredient',
    ALLERGEN: 'Allergen',
    ADDITIVE: 'Additive',
    FUNCTION_CLASS: 'Functional class',
    ORIGIN_STATEMENT: 'Origin statement',
    COLLECTIVE_DESIGNATION: 'Collective designation',
    WARNING: 'Warning',
    CLAIM: 'Claim',
    PRODUCT_DESIGNATION: 'Legal/product designation',
    QUID_RULE: 'QUID rule',
  },
  columns: {
    number: 'Number',
    product: 'Product',
    market: 'Market',
    version: 'Version',
    status: 'Status',
    issues: 'Errors / warnings',
    updated: 'Updated',
    key: 'Key',
    type: 'Type',
    name: 'Name',
    validity: 'Validity',
    path: 'Source / path',
    weight: 'Relevant weight',
    percentage: 'Share',
    flags: 'Flags',
    field: 'Field',
    recipe: 'Recipe',
  },
  fields: {
    key: 'Stable key',
    type: 'Domain type',
    code: 'Code / E number',
    metadata: 'Configurable metadata (JSON)',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    changeReason: 'Change reason',
    name: 'Name',
    synonyms: 'Synonyms, comma-separated',
    description: 'Description',
    reason: 'Review or decision note',
    recipe: 'Approved recipe version',
    number: 'Declaration number (optional)',
    market: 'Market/ruleset key',
    ingredients: 'Ingredient list',
    allergens: 'Allergen statement',
    additives: 'Additives',
    warnings: 'Warnings and origin',
    productDesignation: 'Legal/product designation',
    ingredientText: 'Ingredient list',
    allergenText: 'Allergen statement',
    additiveText: 'Additive text',
    warningText: 'Warnings and origin',
    overrideReason: 'Reason for manual deviation',
    warningAcknowledgement: 'Reasoned acknowledgement of all remaining warnings',
  },
  catalog: {
    title: 'Configurable declaration catalogue',
    subtitle:
      'Versioned ingredients, allergens, additives, functional classes, rules and language-specific terms.',
    empty: 'No catalogue version found.',
    createTitle: 'Create catalogue entry',
    editTitle: 'Edit catalogue version v{{version}}',
    editorHelp:
      'Numbers in metadata are stored as decimal strings. All supported languages must be complete before review.',
    historyTitle: 'Version history: {{key}}',
    historyDescription: 'Approved terms remain immutable; changes are created as a new version.',
  },
  generate: {
    title: 'Generate declaration proposal',
    description:
      'The engine reproducibly resolves the approved BOM, sub-recipes and compound ingredients.',
    legalWarning:
      'This is not an automatic legal approval. The result requires independent expert review and explicit publication.',
  },
  detail: {
    subtitle: 'Version {{version}} · Market {{market}} · Source {{recipe}} v{{recipeVersion}}',
  },
  legal: {
    title: 'Expert and legal review required',
    description:
      'The calculated proposal, QUID flags, claims, warnings and translations must be reviewed by qualified people before legally binding use.',
  },
  summary: {
    status: 'Workflow status',
    ingredients: 'Ingredient items',
    allergens: 'Allergen findings',
    errors: 'Validation errors',
    warnings: 'Warnings',
  },
  tabs: {
    preview: 'Language preview',
    comparison: 'Language comparison',
    sources: 'Sources and snapshot',
    validation: 'Validation',
    history: 'Version history',
  },
  sections: {
    ingredients: 'Ingredients sorted by weight',
    allergens: 'Derived allergens',
    additives: 'Additives and functional classes',
    sourceSnapshot: 'Immutable source evidence',
    validation: 'Validation and data quality',
    history: 'Declaration revisions',
  },
  preview: {
    ingredientText: 'Effective text to be approved for this language.',
    sorting: 'Order and percentages were calculated exactly from the declaration weight.',
    unknownWarning: '“Unknown” is never treated as “does not contain”.',
  },
  comparison: {
    title: 'Compare all supported languages',
    description: 'Amber values are reasoned deviations from the automatically calculated text.',
    automatic: 'Show automatically calculated text',
  },
  sources: {
    description: 'Concrete approved versions and the hash make the calculation reproducible later.',
    approvedAt: 'Recipe approved at',
    profiles: 'Material profile versions',
    catalogVersions: 'Catalogue versions',
    materialSources: 'Material sources',
  },
  validation: {
    description:
      'Errors block review. Warnings must be explicitly assessed and acknowledged before approval.',
  },
  history: { description: 'Published versions remain unchanged as complete snapshots.' },
  states: {
    noAllergens: 'No allergen findings from approved source data.',
    noAdditives: 'No additives from approved source data.',
    noIssues: 'No validation errors or data-quality warnings.',
    notFound: 'The declaration version was not found.',
  },
  override: {
    title: 'Document manual deviations',
    description:
      'Automatic texts remain in the snapshot. Every change is recorded as a deviation with a shared technical reason.',
  },
  publish: {
    confirmation:
      'Confirmation publishes exactly this approved, immutable snapshot. Publication is not a substitute for external legal advice.',
  },
  actionDialog: {
    APPROVE: {
      title: 'Approve declaration',
      description:
        'Independent approval freezes this version. Remaining warnings require a traceable acknowledgement.',
    },
    COPY: {
      title: 'Create catalogue version',
      description: 'The content is copied into a new editable draft.',
    },
    ARCHIVE: {
      title: 'Archive version',
      description: 'The version remains audit-proof but is no longer active for new use.',
    },
    REVIEW: {
      title: 'Complete expert review',
      description: 'Document independent review of sources, calculation, language and warnings.',
    },
    PUBLISH: {
      title: 'Explicitly publish declaration',
      description: 'Publication is a separate, legally relevant step after approval.',
    },
  },
  messages: {
    generated: 'Declaration proposal was generated reproducibly.',
    saved: 'Catalogue version saved.',
    overrideSaved: 'Manual deviations were recorded.',
    submitted: 'Declaration was submitted for independent review.',
    workflow: 'Workflow was updated with an audit trail.',
  },
} as const;
